/* eslint-disable no-underscore-dangle */
/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React, {
  useRef,
  createRef,
  forwardRef,
  useState,
  useEffect,
  cloneElement,
  createContext,
  useImperativeHandle,
} from "react";
import styled, { css } from "styled-components";

const TabsCntx = createContext();

function Tabs({ children }) {
  // Separate tabs and contents
  const tabs = React.Children.toArray(children).filter(
    item => item.type && item.type.displayName === "Tab",
  );
  const contents = React.Children.toArray(children).filter(
    item => item.type && item.type.displayName === "TabContent",
  );
  const tabsRefs = useRef(tabs.map(() => createRef()));
  const contentsRefs = useRef(contents.map(() => createRef()));

  // Get array of tabKey
  const tabKeys = tabs.map(tab => tab.props.tabKey);

  // Find active by hash or defaultActive
  const hashTabKey = typeof window !== "undefined"
    ? decodeURIComponent(window.location.hash.replace(/^#/, ""))
    : null;

  let initialActiveTabKey = tabKeys.includes(hashTabKey) && hashTabKey ? hashTabKey : null;
  if (!initialActiveTabKey) {
    // defaultActive
    const defaultTab = tabs.find(tab => tab.props.defaultActive && !tab.props.hidden);
    initialActiveTabKey = defaultTab ? defaultTab.props.tabKey : null;
  }
  if (!initialActiveTabKey) {
    // first visible tab
    const firstTab = tabs.find(tab => !tab.props.hidden);
    initialActiveTabKey = firstTab ? firstTab.props.tabKey : tabKeys[0];
  }

  const [activeTabKey, setActiveTabKey] = useState(initialActiveTabKey);

  // Listen hashchange
  useEffect(() => {
    const onHashChange = () => {
      const key = decodeURIComponent(window.location.hash.replace(/^#/, ""));
      if (tabKeys.includes(key) && key !== activeTabKey) setActiveTabKey(key);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
    // eslint-disable-next-line
  }, [activeTabKey, tabKeys.join(",")]);

  // Sync hash with tab change
  useEffect(() => {
    const nextHash = `#${encodeURIComponent(activeTabKey || "")}`;
    if (window.location.hash !== nextHash) {
      window.history.replaceState(null, "", nextHash);
    }
  }, [activeTabKey]);

  // Callback when tab changes
  const onTabSelect = (tabKey) => {
    if (!tabKey) return;
    setActiveTabKey(tabKey);

    tabsRefs.current.forEach(ref => ref.current && ref.current._setInactive && ref.current._setInactive(),
    );
    contentsRefs.current.forEach(ref => ref.current && ref.current._setInactive && ref.current._setInactive(),
    );
    const tabIdx = tabKeys.indexOf(tabKey);
    if (tabsRefs.current[tabIdx] && tabsRefs.current[tabIdx].current) {
      tabsRefs.current[tabIdx].current._setActive();
    }
    const contIdx = contents.findIndex(
      c => c.props.contentFor === tabKey,
    );
    if (contentsRefs.current[contIdx] && contentsRefs.current[contIdx].current) {
      contentsRefs.current[contIdx].current._setActive();
    }
  };

  // При первом рендере выставляем нужное состояние
  useEffect(() => {
    onTabSelect(activeTabKey);
    // eslint-disable-next-line
  }, [activeTabKey]);

  return (
    <TabsCntx.Provider value={{ onTabSelect, activeTabKey }}>
      <TabsComponent className="tabs-component">
        <div className="controls">
          <div className="controls-wrapper">
            {tabs.map((tab, key) => cloneElement(tab, {
              ...tab.props,
              key: tab.props.tabKey,
              ref: tabsRefs.current[key],
              active: tab.props.tabKey === activeTabKey,
              onSelect: () => onTabSelect(tab.props.tabKey),
            }),
            )}
          </div>
        </div>
        {contents.map((content, key) => cloneElement(content, {
          ...content.props,
          key: content.props.contentFor,
          ref: contentsRefs.current[key],
          active: content.props.contentFor === activeTabKey,
        }),
        )}
      </TabsComponent>
    </TabsCntx.Provider>
  );
}

function Tab(props, ref) {
  const {
    children,
    icon,
    link,
    hidden = false,
    active: isActive = false,
    onSelect,
  } = props;
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(isActive);

  useEffect(() => {
    setActive(isActive);
  }, [isActive]);

  const gotoLink = (destLink) => {
    window.location = destLink;
  };

  const mouseEvents = {
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
    onClick: () => (link ? gotoLink(link) : (onSelect && onSelect())),
  };

  const normalIcon = icon && cloneElement(icon, { ...icon.props, color: "#2F80ED" });
  const hoverIcon = icon && cloneElement(icon, { ...icon.props, color: "#fff" });

  useImperativeHandle(ref, () => ({
    _setInactive: () => setActive(false),
    _setActive: () => setActive(true),
    _isActive: () => active && !hidden,
  }));

  if (hidden) return null;

  return (
    <TabComponent
      active={active}
      hidden={hidden}
      className="control"
      {...mouseEvents}
    >
      {icon && (
        <div className="icon">{hover || active ? hoverIcon : normalIcon}</div>
      )}
      <div className="content">{children}</div>
    </TabComponent>
  );
}

function TabContent(props, ref) {
  const {
    children,
    active: isActive = false,
    useWrapper = false,
    hidden,
  } = props;
  const [active, setActive] = useState(isActive);

  useEffect(() => setActive(isActive), [isActive]);

  useImperativeHandle(ref, () => ({
    _setInactive: () => setActive(false),
    _setActive: () => setActive(true),
  }));

  if (hidden) return null;

  if (useWrapper) {
    return (
      <TabContentComponent active={active} ref={ref} className="content">
        {children}
      </TabContentComponent>
    );
  }

  // Если children массив элементов — пробежим по нему
  if (Array.isArray(children)) {
    return (
      <>
        {children.map((child, idx) => {
          if (React.isValidElement(child)) {
            return cloneElement(child, { active, key: child.key !== undefined && child.key !== null ? child.key : idx });
          }
          return child;
        })}
      </>
    );
  }

  // Если children только React-элемент
  if (React.isValidElement(children)) {
    return cloneElement(children, { active });
  }

  // Если children строка или что-то еще
  return <>{children}</>;
}

const forwardRefTabs = forwardRef((props, ref) => <Tabs {...props} forwardedRef={ref} />);
const forwardRefTab = forwardRef(Tab);
const forwardRefTabContent = forwardRef(TabContent);

forwardRefTabs.displayName = "Tabs";
forwardRefTab.displayName = "Tab";
forwardRefTabContent.displayName = "TabContent";

export {
  forwardRefTabs as Tabs,
  forwardRefTab as Tab,
  forwardRefTabContent as TabContent,
  TabsCntx,
};

const TabsComponent = styled.div`
  position: relative;
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
  align-items: stretch;
  justify-content: flex-start;

  .controls {
    display: inline-flex;
    align-self: flex-start;
    border: #f2f2f2 solid 1px;
    margin-right: 20px;
    border-radius: 4px;
    overflow: hidden;
    user-select: none;
  }
`;

const TabComponent = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: start;
  font: 14px/1 Roboto;
  color: #2f80ed;
  padding: 0 24px;
  min-height: 52px;
  background: #fff;
  cursor: pointer;
  border-bottom: #f2f2f2 solid 1px;

  .icon {
    font-size: 18px;
    margin-right: 16px;
  }

  &:hover {
    background: #2f80ed;
    color: #fff;
  }

  ${props => props.active
    && css`
      background: #2f80ed;
      color: #fff;
    `}

  ${props => props.hidden
    && css`
      display: none !important;
      visibility: hidden !important;
    `}

  &:last-child {
    border: none 0;
  }
`;

const TabContentComponent = styled.div`
  position: relative;
  display: none;
  ${props => props.active && css`display: block;`}
`;
